import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import Svg, { Path } from 'react-native-svg';
import type { CloudinaryTransformConfig } from '@sweetgreen/garnish';
import {
  BodyText,
  CloudinaryImage,
  LabelText,
  LoadingPlaceholder,
  noop,
  theme,
  truncateText,
  usePressableState,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLineItemsNames } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import type { useOrders } from '@order/Order';

import { useReorderFromOrder } from '../../../screens/ReorderingScreen';

// ────────────────────────────────────────────────────────────────────────────────

export const ReorderRailCard = (props: OrderCardProps) => {
  const { order, orderType = 'custom' } = props;
  const productsCount =
    orderType === 'standard'
      ? order.lineItems.reduce(
          (total, lineItem) => Number(total) + Number(lineItem.quantity),
          0,
        )
      : 0;

  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);
  const { minWidth } = useResponsive();
  const { formatPrice } = useLocalizationContext();

  // ────────────────────────────────────────────────────────────────────

  const cardTextSize: ComponentProps<typeof BodyText>['size'] = minWidth.isMD
    ? 3
    : 4;

  const cardWrapperStyle = minWidth.isMD
    ? styles.wrapperDesktop
    : styles.wrapperMobile;

  const cardImgWrapperStyle = [
    styles.imageWrapper,
    minWidth.isMD ? styles.imageWrapperDesktop : styles.imageWrapperMobile,
  ];
  const assetStyle = minWidth.isMD
    ? styles.assetWrapperDesktop
    : styles.assetWrapperMobile;
  const imageDecalWrapperStyle = [
    styles.imageDecalWrapper,
    StyleSheet.absoluteFill,
  ];

  // ─── Image ───────────────────────────────────────────────────────

  const imageSize = minWidth.isMD
    ? REORDER_RAIL_CARD_SIZE_DESKTOP
    : REORDER_RAIL_CARD_SIZE_MOBILE;
  const imageTransformConfig = useMemo<CloudinaryTransformConfig>(
    () => ({ crop: 'fill', width: imageSize, height: imageSize }),
    [imageSize],
  );

  // ────────────────────────────────────────────────────────────────────

  const orderLineItemsNames = useLineItemsNames(order.lineItems);
  const truncatedOrderLineItemsNames = truncateText(orderLineItemsNames, 40);

  const onReorder = useReorderFromOrder(order);

  return (
    <TouchableOpacity
      ref={ref}
      accessibilityRole="button"
      onPress={onReorder}
      onLongPress={noop} // prevent unwanted {press} actions while scrolling
      delayLongPress={100}
      activeOpacity={0.7}
      delayPressIn={50}
      style={cardWrapperStyle}
      testID="reorder-rail.card"
    >
      <View style={cardImgWrapperStyle}>
        <View style={imageDecalWrapperStyle}>
          {orderType === 'standard' && (
            <StandardOrderShape isInteracting={isInteracting} />
          )}
          {orderType === 'custom' && (
            <CustomOrderShape isInteracting={isInteracting} />
          )}
          {orderType === 'custom-with-name' && (
            <CustomOrderWithCustomNameShape isInteracting={isInteracting} />
          )}
        </View>

        {productsCount > 1 ? (
          <View style={styles.productsCount}>
            <LabelText size={2}>{productsCount}</LabelText>
          </View>
        ) : null}

        <CloudinaryImage
          baseUrl={order.asset.url}
          resizeMode="contain"
          config={imageTransformConfig}
          style={assetStyle}
        />
      </View>

      <BodyText
        size={cardTextSize}
        style={styles.description}
        numberOfLines={2}
        accessibilityLabel={orderLineItemsNames}
      >
        {truncatedOrderLineItemsNames}
      </BodyText>

      {order.total ? (
        <BodyText size={cardTextSize}>
          {formatPrice(order.total, 'USD')}
        </BodyText>
      ) : null}
    </TouchableOpacity>
  );
};

export const ReorderRailCardPlaceholder = () => {
  const { minWidth } = useResponsive();

  const wrapperStyle = minWidth.isMD
    ? styles.wrapperDesktop
    : styles.wrapperMobile;
  const placeholderHeight = minWidth.isMD ? 322 : 222;

  return (
    <View style={wrapperStyle}>
      <LoadingPlaceholder palette="lightGreen" rowHeight={placeholderHeight} />
    </View>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

export const StandardOrderShape = ({
  isInteracting,
}: {
  readonly isInteracting: boolean;
}) => {
  return (
    <View
      testID="reorder-rail-card-shape-standard"
      style={[
        styles.standardOrderShape,
        isInteracting && styles.interactingStandard,
      ]}
    />
  );
};

export const CustomOrderShape = ({
  isInteracting,
}: {
  readonly isInteracting: boolean;
}) => {
  return (
    <View
      testID="reorder-rail-card-shape-custom"
      style={[
        styles.customOrderShape,
        isInteracting && styles.interactingCustom,
      ]}
    />
  );
};

export const CustomOrderWithCustomNameShape = ({
  isInteracting,
}: {
  readonly isInteracting: boolean;
}) => {
  return (
    <View testID="reorder-rail-card-shape-custom-with-name">
      <Svg width="100%" viewBox="0 0 240 215" fill="none">
        <Path
          d="M236.32 93.9265C241.227 102.407 241.227 112.856 236.32 121.337L190.055 201.295C185.148 209.776 176.079 215 166.265 215L73.7351 215C63.9209 215 54.8523 209.776 49.9452 201.295L3.68032 121.337C-1.22675 112.856 -1.22675 102.407 3.68031 93.9265L49.9452 13.9683C54.8523 5.48753 63.921 0.263183 73.7351 0.263184L166.265 0.263192C176.079 0.263193 185.148 5.48754 190.055 13.9683L236.32 93.9265Z"
          fill={
            isInteracting ? theme.colors.CUCUMBER_HOVER : theme.colors.CUCUMBER
          }
        />
      </Svg>
    </View>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type OrderCardProps = {
  readonly order: ReturnType<typeof useOrders>['orders'][number];
  readonly orderType?: 'standard' | 'custom' | 'custom-with-name';
};

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const REORDER_RAIL_CARD_SIZE_MOBILE = 152;
const REORDER_RAIL_CARD_SIZE_DESKTOP = 240;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapperMobile: {
    width: REORDER_RAIL_CARD_SIZE_MOBILE,
  },

  wrapperDesktop: {
    width: REORDER_RAIL_CARD_SIZE_DESKTOP,
  },

  imageWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: theme.spacing['4'],
  },

  imageWrapperMobile: {
    height: REORDER_RAIL_CARD_SIZE_MOBILE,
  },

  imageWrapperDesktop: {
    height: REORDER_RAIL_CARD_SIZE_DESKTOP,
  },

  imageDecalWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  productsCount: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: theme.spacing['2'],
    left: theme.spacing['2'],
    width: 24,
    height: 24,
    borderRadius: 48,
    borderWidth: 1,
    color: theme.colors.TEXT_COLOR,
    borderColor: theme.colors.TEXT_COLOR,
  },

  assetWrapperDesktop: {
    width: REORDER_RAIL_CARD_SIZE_DESKTOP,
    height: REORDER_RAIL_CARD_SIZE_DESKTOP,
  },
  assetWrapperMobile: {
    width: REORDER_RAIL_CARD_SIZE_MOBILE,
    height: REORDER_RAIL_CARD_SIZE_MOBILE,
  },

  description: {
    textTransform: 'capitalize',
  },

  standardOrderShape: {
    width: '100%',
    height: '100%',
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.QUINOA,
  },

  customOrderShape: {
    width: '100%',
    height: '100%',
    borderRadius: REORDER_RAIL_CARD_SIZE_DESKTOP,
    backgroundColor: theme.colors.SKY,
  },

  interactingStandard: {
    backgroundColor: theme.colors.QUINOA_HOVER,
  },

  interactingCustom: {
    backgroundColor: theme.colors.SKY_HOVER,
  },
});
